import { AxiosInstance, AxiosRequestConfig } from 'axios';
import { INewsItem } from '@project/store/News/types';
import { GeoJSONFeatureCollection } from 'ol/format/GeoJSON';
import { News } from '@datum-group/isogd-openapi/lib';
import { UploadFile } from 'antd/lib/upload/interface';

export interface Photo {
  id: number;
  description: string;
  photo: string;
}

export interface NewsResponse {
  id: number;
  title: string;
  description: string;
  address: string;
  is_checked: boolean;
  link: string;
  is_archived: boolean;
  author: string;
  photos: Photo[];
  date_start: string;
  date_end: string;
  date_pub: string;
  date_planning: string | null;
  geom: string | null;
  category: number | null;
  user_id: number;
}

export const dateRangeKeys: [string, string] = ['date_start', 'date_end'];

export interface Categories {
  id: number;
  name: string;
}

export interface NewsCollectionResponse {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: Array<NewsResponse>;
}

export interface CategoriesCollectionResponse {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: Array<Categories>;
}

export class NewsCategories {
  readonly url = 'api/open-news-categories/?_page_size=0&_order=name';

  constructor(private _axios: AxiosInstance) {}

  get = async (): Promise<Categories[]> => {
    const response = await this._axios.get<CategoriesCollectionResponse>(
      `${this.url}`,
    );

    // @ts-ignore
    return response.data.results;
  };
}

export class NewsPhotos {
  readonly url = 'api/open-news-files';

  constructor(private _axios: AxiosInstance) {}

  async post(file: UploadFile, newsId: number): Promise<void> {
    let formData = new FormData();

    formData.set('photo', file.originFileObj as File);

    return await this._axios.post(`/${this.url}/${newsId}/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  async delete(fileId: number): Promise<void> {
    return await this._axios.delete(`/${this.url}/${fileId}/`);
  }
}

export class NewsGeoJSONLayer {
  readonly url = 'api/open-news-geojson';

  constructor(private _axios: AxiosInstance) {}

  async get(
    config?: AxiosRequestConfig,
  ): Promise<{ type: 'FeatureCollection'; features: INewsItem[] }> {
    const response = await this._axios.get<GeoJSONFeatureCollection>(
      `${this.url}/`,
      config,
    );

    // @ts-ignore
    return response.data;
  }
}

export class NewsApi {
  readonly url = 'api/open-news';

  constructor(private _axios: AxiosInstance) {}

  async get(params?: URLSearchParams, signal?: AbortSignal) {
    const response = await this._axios.get<NewsCollectionResponse>(
      `${this.url}/`,
      { params, signal },
    );

    // @ts-ignore
    return response.data;
  }

  async create(news: Partial<NewsResponse>): Promise<NewsResponse> {
    const response = await this._axios.post<NewsResponse>(`${this.url}/`, {
      ...news,
    });

    // @ts-ignore
    return response.data;
  }

  async delete(id: number): Promise<NewsCollectionResponse> {
    const response = await this._axios.delete<NewsCollectionResponse>(
      `${this.url}/${id}/`,
    );

    // @ts-ignore
    return response.data.results;
  }

  async patch(
    news: Partial<Omit<NewsResponse, 'geom'>> & {
      id: number;
      geom?: string;
    },
  ): Promise<NewsResponse> {
    const response = await this._axios.patch<NewsResponse>(
      `${this.url}/${news.id}/`,
      { ...news },
    );

    // @ts-ignore
    return response.data;
  }
}

export class NewsBulkPublication {
  readonly url = 'api/open-news-geojson/bulk_publish/';

  constructor(private _axios: AxiosInstance) {}

  put = async (params: number[]): Promise<News[]> => {
    let newUrl = this.url;
    if (params) {
      newUrl += `?id__in=${params}`;
    }

    const response = await this._axios.put<News>(`${newUrl}`);

    // @ts-ignore
    return response.data.results;
  };
}
